import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';
import { Modal } from './modal';

/**
 * In-app confirmation and reason dialogs, replacing `window.confirm` and
 * `window.prompt`.
 *
 * Every destructive or hard-to-undo action (delete, deactivate, block, reject,
 * suspend) asks first through this, so the console speaks with one voice: the
 * same v2 modal, the action named on the button ("Delete banner", not "OK"),
 * and danger styling when it destroys something.
 *
 *   const { confirm, prompt } = useConfirm();
 *   if (!(await confirm({ title: 'Delete banner?', confirmLabel: 'Delete', tone: 'danger' }))) return;
 *   const reason = await prompt({ title: 'Reject product?', label: 'Reason', required: true });
 *   if (reason === null) return; // cancelled
 */

export interface ConfirmOptions {
  title: ReactNode;
  /** One or two sentences on what happens, and whether it can be undone. */
  description?: ReactNode;
  /** Verb for the button, e.g. "Delete", "Deactivate". Defaults to "Confirm". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` for anything that removes, blocks or rejects. */
  tone?: 'danger' | 'primary';
}

export interface PromptOptions extends ConfirmOptions {
  /** Field label, e.g. "Reason for rejection". */
  label: string;
  placeholder?: string;
  defaultValue?: string;
  /** Blank input disables the confirm button. Defaults to true. */
  required?: boolean;
  multiline?: boolean;
  /** Shown under the field. */
  help?: ReactNode;
}

interface ConfirmApi {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  /** Resolves to the trimmed text, or null when cancelled. */
  prompt: (opts: PromptOptions) => Promise<string | null>;
}

type Pending =
  | { kind: 'confirm'; opts: ConfirmOptions; resolve: (v: boolean) => void }
  | { kind: 'prompt'; opts: PromptOptions; resolve: (v: string | null) => void };

const ConfirmContext = createContext<ConfirmApi | null>(null);

export function ConfirmProvider({ children }: PropsWithChildren) {
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>((resolve) => setPending({ kind: 'confirm', opts, resolve })),
    [],
  );
  const prompt = useCallback(
    (opts: PromptOptions) =>
      new Promise<string | null>((resolve) => setPending({ kind: 'prompt', opts, resolve })),
    [],
  );

  const close = (value: boolean | string | null) => {
    if (!pending) return;
    if (pending.kind === 'confirm') pending.resolve(value === true);
    else pending.resolve(typeof value === 'string' ? value : null);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      {pending && <ConfirmDialog pending={pending} onClose={close} />}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmApi {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}

function ConfirmDialog({
  pending,
  onClose,
}: {
  pending: Pending;
  onClose: (value: boolean | string | null) => void;
}) {
  const { opts } = pending;
  const isPrompt = pending.kind === 'prompt';
  const promptOpts = isPrompt ? (pending.opts as PromptOptions) : null;
  const [text, setText] = useState(promptOpts?.defaultValue ?? '');
  const confirmRef = useRef<HTMLButtonElement>(null);
  const fieldRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  const required = promptOpts ? promptOpts.required !== false : false;
  const canConfirm = !isPrompt || !required || text.trim().length > 0;
  const danger = (opts.tone ?? 'danger') === 'danger';

  const submit = () => {
    if (!canConfirm) return;
    onClose(isPrompt ? text.trim() : true);
  };

  useEffect(() => {
    // Focus the field for a reason, otherwise Cancel-safe: the confirm button
    // gets focus so Enter confirms deliberately, Escape always cancels.
    (isPrompt ? fieldRef.current : confirmRef.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose(isPrompt ? null : false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // Mount-only: the dialog is remounted for every request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fieldId = 'confirm-dialog-field';

  return (
    <Modal
      title={opts.title}
      description={opts.description}
      onClose={() => onClose(isPrompt ? null : false)}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={() => onClose(isPrompt ? null : false)}>
            {opts.cancelLabel ?? 'Cancel'}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            disabled={!canConfirm}
            onClick={submit}
          >
            {opts.confirmLabel ?? 'Confirm'}
          </button>
        </>
      }
    >
      {promptOpts && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label htmlFor={fieldId} className="field-label mb-1.5 block">
            {promptOpts.label}
            {!required && <span className="font-normal text-ink-500"> (optional)</span>}
          </label>
          {promptOpts.multiline ? (
            <textarea
              id={fieldId}
              ref={fieldRef}
              className="input min-h-[92px] py-2.5"
              placeholder={promptOpts.placeholder}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          ) : (
            <input
              id={fieldId}
              ref={fieldRef}
              className="input"
              placeholder={promptOpts.placeholder}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          )}
          {promptOpts.help && <p className="field-help mt-1.5">{promptOpts.help}</p>}
        </form>
      )}
    </Modal>
  );
}
